import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ChevronLeft, Search, Check, Radar, Loader2, ArrowRight, Building2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { SOCIAL_META } from "../../lib/sales";

const FIELDS = [
  { key: "company", label: "Company / Agency name", placeholder: "e.g. Marloe & Co", required: true },
  { key: "branch", label: "Branch name", placeholder: "e.g. Wadebridge" },
  { key: "website", label: "Website URL", placeholder: "https://…" },
  { key: "postcode", label: "Postcode / Location", placeholder: "e.g. PL27 7AL" },
];

export default function HealthCheckNew() {
  const navigate = useNavigate();
  const [prospect, setProspect] = useState({ company: "", branch: "", website: "", postcode: "" });
  const [isNc, setIsNc] = useState(false);
  const [socials, setSocials] = useState({ instagram: "", facebook: "", linkedin: "", tiktok: "", youtube: "" });
  const [id, setId] = useState(null);
  const [discovering, setDiscovering] = useState(false);
  const [discovered, setDiscovered] = useState(null);
  const [running, setRunning] = useState(false);

  const setField = (k, v) => setProspect((p) => ({ ...p, [k]: v }));
  const setSocial = (k, v) => setSocials((s) => ({ ...s, [k]: v }));

  async function ensureDraft() {
    if (id) {
      await api.patch(`/sales/health-checks/${id}`, { prospect, is_nichecom_customer: isNc, socials });
      return id;
    }
    const r = await api.post("/sales/health-checks", { prospect, is_nichecom_customer: isNc, socials });
    setId(r.data.id);
    return r.data.id;
  }

  async function findAccounts() {
    if (!prospect.website) { toast.error("Enter a website URL first, or add accounts manually."); return; }
    setDiscovering(true); setDiscovered(null);
    try {
      const hcId = await ensureDraft();
      const r = await api.post(`/sales/health-checks/${hcId}/discover`, { website: prospect.website });
      const found = r.data.found || {};
      setDiscovered(found);
      if (r.data.error) toast.error(r.data.error);
      else if (Object.keys(found).length === 0) toast("No linked accounts found on the site — add them manually.");
      else toast.success(`Found ${Object.keys(found).length} account(s) — confirm to add.`);
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
    finally { setDiscovering(false); }
  }

  const confirmDiscovered = (k) => { setSocial(k, discovered[k].handle || discovered[k].url); };

  async function run() {
    if (!prospect.company) { toast.error("Company name is required."); return; }
    setRunning(true);
    try {
      const hcId = await ensureDraft();
      navigate(`/sales/check/${hcId}/analysis`);
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); setRunning(false); }
  }

  const hasSocials = Object.values(socials).some(Boolean);

  return (
    <div>
      <header className="sticky top-0 z-20 flex items-center gap-4 px-6 sm:px-8 py-4 border-b backdrop-blur-md" style={{ background: "rgba(247,248,246,.85)", borderColor: "#E7E8E4" }}>
        <Link to="/sales" className="inline-flex items-center gap-1.5 text-sm text-ink-2 hover:text-ink" data-testid="new-back"><ChevronLeft size={16} /> Sales Director</Link>
        <h1 className="text-lg font-bold tracking-tight text-ink ml-2">New Social Health Check</h1>
      </header>

      <div className="px-6 sm:px-8 py-8 max-w-3xl">
        {/* Prospect */}
        <div className="card p-6 sm:p-8">
          <div className="flex items-center gap-2.5 mb-5">
            <span className="grid place-items-center w-9 h-9 rounded-lg" style={{ background: "var(--grad-green-soft)" }}><Building2 size={17} strokeWidth={1.75} style={{ color: "#5F8213" }} /></span>
            <div><div className="label-caps">Step 1</div><div className="text-base font-bold text-ink">Prospect details</div></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {FIELDS.map((f) => (
              <label key={f.key} className={f.key === "website" || f.key === "company" ? "sm:col-span-2" : ""}>
                <span className="text-sm font-medium text-ink">{f.label}{f.required && <span style={{ color: "#C0473F" }}> *</span>}</span>
                <input value={prospect[f.key]} onChange={(e) => setField(f.key, e.target.value)} placeholder={f.placeholder}
                  data-testid={`prospect-${f.key}`} className="mt-1.5 w-full rounded-xl border px-4 py-2.5 text-sm focus-ring" style={{ borderColor: "#E7E8E4", background: "#fff" }} />
              </label>
            ))}
          </div>
          <label className="flex items-center gap-3 mt-5 cursor-pointer select-none" data-testid="prospect-nichecom">
            <button type="button" onClick={() => setIsNc((v) => !v)} className="relative w-11 h-6 rounded-full transition-colors focus-ring" style={{ background: isNc ? "#93C020" : "#D6D7D0" }}>
              <span className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform" style={{ transform: isNc ? "translateX(20px)" : "none" }} />
            </button>
            <span className="text-sm text-ink">This prospect is already a <b>NicheCom</b> customer</span>
          </label>
        </div>

        {/* Socials */}
        <div className="card p-6 sm:p-8 mt-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2.5">
              <span className="grid place-items-center w-9 h-9 rounded-lg" style={{ background: "var(--grad-green-soft)" }}><Radar size={17} strokeWidth={1.75} style={{ color: "#5F8213" }} /></span>
              <div><div className="label-caps">Step 2</div><div className="text-base font-bold text-ink">Social accounts</div></div>
            </div>
            <button onClick={findAccounts} disabled={discovering} data-testid="find-accounts" className="btn-secondary focus-ring">
              {discovering ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} strokeWidth={1.75} />} Find social accounts
            </button>
          </div>

          {discovered && Object.keys(discovered).length > 0 && (
            <div className="rounded-xl p-4 mb-5" style={{ background: "var(--grad-green-soft)", border: "1px solid rgba(147,192,32,.3)" }} data-testid="discovered-box">
              <div className="label-caps mb-2" style={{ color: "#5F8213" }}>Discovered on the website — confirm to add</div>
              <div className="space-y-2">
                {Object.entries(discovered).map(([k, v]) => {
                  const M = SOCIAL_META[k]; const added = socials[k];
                  return (
                    <div key={k} className="flex items-center gap-3 bg-white rounded-lg px-3 py-2">
                      {M?.icon && <M.icon size={16} className="text-ink-2" />}
                      <span className="text-sm text-ink truncate flex-1">{v.handle || v.url}</span>
                      <button onClick={() => confirmDiscovered(k)} data-testid={`confirm-${k}`} className="text-xs font-semibold inline-flex items-center gap-1" style={{ color: added ? "#5F8213" : "#2F6BFF" }}>
                        {added ? <><Check size={13} /> Added</> : "Confirm"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="text-sm text-ink-2 mb-3">Or enter handles / URLs manually — this always works as a fallback.</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Object.keys(SOCIAL_META).map((k) => {
              const M = SOCIAL_META[k];
              return (
                <label key={k} className="flex items-center gap-2 rounded-xl border px-3 py-2" style={{ borderColor: "#E7E8E4", background: "#fff" }}>
                  <M.icon size={16} className="text-ink-3 shrink-0" />
                  <input value={socials[k]} onChange={(e) => setSocial(k, e.target.value)} placeholder={M.label} data-testid={`social-${k}`}
                    className="w-full text-sm focus:outline-none bg-transparent" />
                </label>
              );
            })}
          </div>
        </div>

        {/* Run */}
        <div className="sticky bottom-0 mt-6 py-4">
          <button onClick={run} disabled={running || !prospect.company} data-testid="run-health-check"
            className="btn-primary focus-ring w-full justify-center text-base !py-3.5 disabled:opacity-50">
            {running ? <><Loader2 size={18} className="animate-spin" /> Preparing…</> : <>Run Social Health Check <ArrowRight size={18} strokeWidth={2} /></>}
          </button>
          {!hasSocials && <p className="text-center text-xs text-ink-3 mt-2">Tip: add at least one social account for a richer analysis.</p>}
        </div>
      </div>
    </div>
  );
}
